/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/oAuth/OAuthPanel" />
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Uri } from "VSS/Platform/Util/Url";
import { Button } from "VSSUI/Button";
import { Dropdown } from "VSSUI/Dropdown";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { css } from "VSSUI/Util";
import { getConnectedServiceClient } from "Wit/Clients/ConnectedService/Client/RestClient/ConnectedService";
import { getServiceEndpointClient } from "Wit/Clients/ServiceEndpoint/Client/RestClient/ServiceEndpoint";
import { OAuthConfiguration } from "Wit/Clients/ServiceEndpoint/Types/Contracts/ServiceEndpoint";
import { BasePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/BasePanel";
import { IOAuthPanelProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/oAuth/OAuthPanel.Props";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";

interface IOAuthPanelState {
    oAuthConfigurations: OAuthConfiguration[];
    selectedConfiguration?: OAuthConfiguration;
    errorMessage?: string;
    loadingSpinnerMessage?: string;
}

export class OAuthPanel extends VssComponent<IOAuthPanelProps, IOAuthPanelState> {
    private _isMounted = false;

    constructor(props: IOAuthPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            oAuthConfigurations: [],
            errorMessage: undefined
        };
    }

    public componentDidMount() {
        this._isMounted = true;
        this._getOAuthConfigurations();
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        return (
            <BasePanel
                onDismiss={this.props.onDismiss}
                title={Resources.EnterpriseOAuth_title}
                onRenderHeaderContent={this._renderHeader}
                onRenderPanelFooter={this._renderFooter}
                className="oauth-panel"
                errorMessage={this.state.errorMessage}
                onDismissErrorMessage={this._onDismissErrorMessage}
                loadingSpinnerMessage={this.state.loadingSpinnerMessage}
            >
                {this._renderDropdown()}
                {this._renderMessage()}
            </BasePanel>
        );
    }

    private readonly _getOAuthConfigurations = (): void => {
        this.setState(
            {
                loadingSpinnerMessage: Resources.EnterpriseOAuth_SpinnerGetOAuthConfigurations
            },
            async () => {
                try {
                    const serviceClient = getServiceEndpointClient(this.context.pageContext);
                    const configurations = await serviceClient.getOAuthConfigurations();
                    const gheConfigurations =
                        configurations && configurations.filter(c => c.endpointType === GitHubConnectionConstants.GitHubEnterpriseEndpointType);
                    this._isMounted &&
                        this.setState({
                            loadingSpinnerMessage: undefined,
                            oAuthConfigurations: gheConfigurations || []
                        });
                } catch (error) {
                    this._isMounted &&
                        this.setState({
                            loadingSpinnerMessage: undefined,
                            errorMessage: error.message
                        });
                }
            }
        );
    };

    private readonly _onDismissErrorMessage = () => {
        this.setState({ errorMessage: undefined });
    };

    private readonly _renderHeader = (): JSX.Element => {
        return <div>{Resources.EnterpriseOAuth_subHeader}</div>;
    };

    private readonly _renderFooter = (): JSX.Element => {
        return (
            <>
                <div className="align-right">
                    <Button className="footer-button" text={Resources.Cancel} onClick={this.props.onDismiss} />
                    <Button
                        className="footer-button"
                        text={Resources.Connect}
                        primary={true}
                        disabled={!this._canConnect()}
                        onClick={this._onConnectClick}
                    />
                </div>
            </>
        );
    };

    private readonly _renderDropdown = (): JSX.Element => {
        return (
            <>
                <div className="label fontWeightSemiBold">{Resources.EnterpriseOAuth_dropdownLabel}</div>
                <Dropdown
                    className="oauth-items"
                    placeholder={this._getText()}
                    items={this._getItems()}
                    onSelect={this.onSelect}
                    disabled={this._getDisabled()}
                />
            </>
        );
    };

    private readonly _renderMessage = (): JSX.Element => {
        const routeId = "ms.vss-distributed-task.oauth-configurations-hub-default-route";
        const orgSettingUrl = routeUrl(this.context.pageContext, routeId);
        const messageClassName = "message";
        if (this.state.oAuthConfigurations.length > 0) {
            return (
                <div className={css(messageClassName)}>
                    {Resources.EnterpriseOAuth_manageConfigurations}{" "}
                    <Link target="_blank" href={orgSettingUrl}>
                        {Resources.OrganizationSettings}
                    </Link>
                </div>
            );
        } else {
            return (
                <>
                    <div className={css(messageClassName)}>
                        {Resources.EnterpriseOAuth_followInstruction_Follow}
                        <Link target="_blank" href={GitHubConnectionConstants.GHEOAuthInstructionUrl}>
                            {Resources.EnterpriseOAuth_followInstruction_configureOAuth}
                        </Link>
                        {Resources.EnterpriseOAuth_followInstruction_to}
                    </div>
                    <div className={css(messageClassName)}>
                        {Resources.EnterpriseOAuth_newOAuth_This}
                        <Link target="_blank" href={orgSettingUrl}>
                            {Resources.OrganizationSettings}
                        </Link>
                        {Resources.EnterpriseOAuth_newOAuth_page}
                        <b>{Resources.ClientId}</b>
                        {Resources.EnterpriseOAuth_newOAuth_and}
                        <b>{Resources.ClientSecret}</b>
                        {Resources.EnterpriseOAuth_newOAuth_provided}
                    </div>
                </>
            );
        }
    };

    private readonly _getItems = (): IListBoxItem[] => {
        return this.state.oAuthConfigurations.map(config => {
            return {
                id: config.id,
                text: config.name
            };
        });
    };

    private onSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {
        this.setState({
            selectedConfiguration: this.state.oAuthConfigurations.find((value) => value.id === item.id)
        });
    };

    private readonly _getText = (): string => {
        if (this.state.selectedConfiguration) {
            return this.state.selectedConfiguration.name;
        } else {
            if (this.state.oAuthConfigurations.length > 0) {
                return Resources.EnterpriseOAuth_chooseConnection;
            } else {
                return Resources.EnterpriseOAuth_noConnections;
            }
        }
    };

    private readonly _getDisabled = (): boolean => {
        return this.state.oAuthConfigurations.length === 0;
    };

    private readonly _canConnect = (): boolean => {
        return !!this.state.selectedConfiguration;
    };

    private readonly _onConnectClick = (): void => {
        if (!this.state.selectedConfiguration) {
            // No op if no configuration selected.
            // This should not happen as the button shouldn't be lighted up.
            return;
        }

        this.setState(
            {
                loadingSpinnerMessage: Resources.EnterpriseOAuth_SpinnerConnectingAccountText
            },
            () => this._authenticate()
        );
    };

    private readonly _authenticate = async (): Promise<void> => {
        try {
            const connectedServiceClient = getConnectedServiceClient(this.context.pageContext);
            const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
            const pageData = pageService.getData();
            // tslint:disable-next-line:no-non-null-assertion
            const projectId = pageData!.project!.id;
            const redirectUrl = new Uri(window.location.href);
            redirectUrl.addQueryParam("isGHE", "true"); // For error scenario we know it's GHE
            const queryParams = `redirectUrl=${encodeURIComponent(redirectUrl.absoluteUri)}&redirectOnFailure=true`;
            const authRequest = await connectedServiceClient.createAuthRequest(
                // tslint:disable-next-line:no-non-null-assertion
                undefined! /* As we don't have a previous AuthRequest is ok to pass undefined, that's why we're enforcing it*/,
                projectId,
                "oauth2",
                this.state.selectedConfiguration!.id,
                "" /* For oauth2, the scopes have to be empty */,
                queryParams
            );
            this._isMounted && window.open(authRequest.url, "_self");
        } catch (error) {
            this._isMounted &&
                this.setState({
                    loadingSpinnerMessage: undefined,
                    errorMessage: error.message
                });
        }
    };
}
